import { useEffect, useRef, useCallback } from 'react';
import {
  doc,
  onSnapshot,
  updateDoc,
  writeBatch,
  serverTimestamp,
  collection,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthStore } from '../stores/authStore';
import { usePlanStore } from '../stores/planStore';
import { COLLECTIONS, SUBCOLLECTIONS } from '../lib/collections';
import type { StageName } from '../lib/collections';
import type { BusinessPlan } from '../types/businessPlan';
import { TOKEN_EARNINGS } from '../types/tokens';

function getPlanDocPath(accountId: string, profileId: string, planId: string) {
  return `${COLLECTIONS.USERS}/${accountId}/${SUBCOLLECTIONS.PROFILES}/${profileId}/${SUBCOLLECTIONS.BUSINESS_PLANS}/${planId}`;
}

export function useBusinessPlan(planId: string | undefined) {
  const accountId = useAuthStore((s) => s.accountId);
  const profile = useAuthStore((s) => s.profile);
  const activePlan = usePlanStore((s) => s.activePlan);
  const setActivePlan = usePlanStore((s) => s.setActivePlan);
  const loading = usePlanStore((s) => s.loading);
  const setLoading = usePlanStore((s) => s.setLoading);

  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Subscribe to business plan document.
  // Use accountId/profileId as deps (not full objects) to avoid re-subscribing
  // on every auth token refresh or profile object recreation.
  const profileId = profile?.id;

  useEffect(() => {
    if (!accountId || !profileId || !planId) return;

    setLoading(true);
    const docPath = getPlanDocPath(accountId, profileId, planId);
    const docRef = doc(db, docPath);

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = { id: snapshot.id, ...snapshot.data() } as BusinessPlan;
          setActivePlan(data);
        } else {
          setActivePlan(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error subscribing to business plan:', error);
        setLoading(false);
      },
    );

    return () => {
      unsubscribe();
      // Clear all debounce timers
      Object.values(debounceTimers.current).forEach(clearTimeout);
      debounceTimers.current = {};
    };
  }, [accountId, profileId, planId, setActivePlan, setLoading]);

  // Save a stage response with 500ms debounce
  const saveStageResponse = useCallback(
    (stageName: StageName, questionId: string, value: string) => {
      if (!accountId || !profileId || !planId) return;

      const timerKey = `${stageName}.${questionId}`;
      if (debounceTimers.current[timerKey]) {
        clearTimeout(debounceTimers.current[timerKey]);
      }

      debounceTimers.current[timerKey] = setTimeout(async () => {
        try {
          const docPath = getPlanDocPath(accountId, profileId, planId);
          const docRef = doc(db, docPath);
          await updateDoc(docRef, {
            [`stageResponses.${stageName}.${questionId}`]: value,
            lastModified: serverTimestamp(),
          });
        } catch (error) {
          console.error('Error saving stage response:', error);
        }
      }, 500);
    },
    [accountId, profileId, planId],
  );

  // Save homework field with 500ms debounce
  const saveHomework = useCallback(
    (fieldName: string, value: unknown) => {
      if (!accountId || !profileId || !planId) return;

      const timerKey = `homework.${fieldName}`;
      if (debounceTimers.current[timerKey]) {
        clearTimeout(debounceTimers.current[timerKey]);
      }

      debounceTimers.current[timerKey] = setTimeout(async () => {
        try {
          const docPath = getPlanDocPath(accountId, profileId, planId);
          const docRef = doc(db, docPath);
          await updateDoc(docRef, {
            [fieldName]: value,
            lastModified: serverTimestamp(),
          });
        } catch (error) {
          console.error('Error saving homework:', error);
        }
      }, 500);
    },
    [accountId, profileId, planId],
  );

  // Complete a stage with atomic batch write
  const completeStage = useCallback(
    async (
      stageName: StageName,
      totalQuestions: number,
      timeSpent: number,
      businessName?: string,
    ) => {
      if (!accountId || !profileId || !planId || !activePlan) return;

      const docPath = getPlanDocPath(accountId, profileId, planId);
      const planRef = doc(db, docPath);

      const now = Timestamp.now();
      const completedStages = [...(activePlan.completedStages || []), stageName];

      // Find next stage
      const stageOrder: StageName[] = [
        'ideaExplosion',
        'problemSolver',
        'customerDetective',
        'mvpMadness',
        'moneyMoves',
        'competitionStation',
      ];
      const currentIdx = stageOrder.indexOf(stageName);
      const nextStage: StageName | 'completed' =
        currentIdx < stageOrder.length - 1
          ? stageOrder[currentIdx + 1]
          : 'completed';

      // Update plan document (businessPlans collection allows client writes)
      try {
        await updateDoc(planRef, {
          [`stageProgress.${stageName}`]: {
            isCompleted: true,
            questionsAnswered: totalQuestions,
            totalQuestions,
            timeSpent,
            startedAt: activePlan.stageProgress?.[stageName]?.startedAt ?? now,
            completedAt: now,
          },
          currentStage: nextStage,
          completedStages,
          ...(businessName ? { businessName } : {}),
          lastModified: serverTimestamp(),
        });
      } catch (error) {
        console.error('Error completing stage:', error);
        throw error;
      }

      // Award tokens separately — tokenLedger requires Cloud Functions (Admin SDK)
      // so this is a best-effort client write. If rules block it, stage completion
      // still succeeds above.
      try {
        const currentProfile = useAuthStore.getState().profile;
        const currentBalance = currentProfile?.tokenBalance?.currentBalance ?? 100;
        const ledgerCollectionRef = collection(
          db,
          COLLECTIONS.USERS,
          accountId,
          SUBCOLLECTIONS.PROFILES,
          profileId,
          SUBCOLLECTIONS.TOKEN_LEDGER,
        );

        const batch = writeBatch(db);
        const ledgerRef = doc(ledgerCollectionRef);
        const stageEarnAmount = TOKEN_EARNINGS.COMPLETE_STAGE;
        let newBalance = currentBalance + stageEarnAmount;

        batch.set(ledgerRef, {
          type: 'earned',
          amount: stageEarnAmount,
          feature: `stage_complete_${stageName}`,
          description: `Completed stage: ${stageName}`,
          balanceAfter: newBalance,
          relatedPlanId: planId,
          timestamp: serverTimestamp(),
        });

        // Award bonus tokens when all 6 stages are completed
        const allStagesComplete = nextStage === 'completed';
        if (allStagesComplete) {
          const bonusRef = doc(ledgerCollectionRef);
          newBalance += TOKEN_EARNINGS.COMPLETE_ALL_STAGES;
          batch.set(bonusRef, {
            type: 'earned_bonus',
            amount: TOKEN_EARNINGS.COMPLETE_ALL_STAGES,
            feature: 'all_stages_complete',
            description: 'Bonus: Completed all 6 stages!',
            balanceAfter: newBalance,
            relatedPlanId: planId,
            timestamp: serverTimestamp(),
          });
        }

        await batch.commit();
      } catch (error) {
        // Token write failure should not block stage completion
        console.warn('Token ledger write failed (may require Cloud Function):', error);
      }
    },
    [accountId, profileId, planId, activePlan],
  );

  return {
    plan: activePlan,
    loading,
    saveStageResponse,
    saveHomework,
    completeStage,
  };
}
